import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { brandingConfig } from "../../content/config/branding";
import Switch from "./Switch";
import Button from "./Button";
import { cookieActions } from "../../utils/buttonActions";
import { useDevice } from "../../context/DeviceContext";

/**
 * Componente CookiesBanner para gestión de cookies
 * Implementa dos vistas: banner inicial y configuración expandida
 */
const CookiesBanner = () => {
  const { isMobile } = useDevice();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true, // Siempre activas
    performance: false,
    functional: false,
    marketing: false,
  });

  // Verificar si ya se han configurado las cookies
  useEffect(() => {
    const savedPreferences = localStorage.getItem("cookiePreferences");
    if (!savedPreferences) {
      setIsVisible(true);
    } else {
      setCookiePreferences(JSON.parse(savedPreferences));
    }
  }, []);

  /**
   * Maneja la aceptación de todas las cookies
   */
  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      performance: true,
      functional: true,
      marketing: true,
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem("cookiePreferences", JSON.stringify(allAccepted));
    setIsVisible(false);

    // Ejecutar función personalizable para desarrolladores
    cookieActions.acceptAll();
  };

  /**
   * Maneja el rechazo de todas las cookies (excepto esenciales)
   */
  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      performance: false,
      functional: false,
      marketing: false,
    };
    setCookiePreferences(onlyEssential);
    localStorage.setItem("cookiePreferences", JSON.stringify(onlyEssential));
    setIsVisible(false);

    // Ejecutar función personalizable para desarrolladores
    cookieActions.rejectAll();
  };

  /**
   * Maneja la confirmación de opciones personalizadas
   */
  const handleConfirmOptions = () => {
    localStorage.setItem(
      "cookiePreferences",
      JSON.stringify(cookiePreferences)
    );
    setIsVisible(false);

    // Ejecutar función personalizable para desarrolladores
    cookieActions.confirmOptions();
  };

  /**
   * Maneja el cambio de preferencias individuales
   */
  const handlePreferenceChange = (type, value) => {
    setCookiePreferences((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  /**
   * Maneja el guardado de preferencias personalizadas
   */
  const handleSavePreferences = () => {
    localStorage.setItem(
      "cookiePreferences",
      JSON.stringify(cookiePreferences)
    );
    setIsVisible(false);

    // Ejecutar función personalizable para desarrolladores
    cookieActions.confirmOptions();
  };

  if (!isVisible) return null;

  // Variantes de animación para el contenedor principal (aparición/desaparición)
  const containerVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: isMobile ? 100 : 50,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      y: isMobile ? 100 : 30,
      transition: { duration: 0.3, ease: "easeIn" },
    },
  };

  // Variantes para la expansión/contracción del contenedor completo con efecto bounce/gelatina
  const expansionVariants = {
    compact: {
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.175, 0.885, 0.32, 1.275], // Efecto gelatina
        type: "spring",
        damping: 12,
        stiffness: 200,
        mass: 0.8,
      },
    },
    expanded: {
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.68, -0.55, 0.265, 1.55], // Efecto bounce más pronunciado
        type: "spring",
        damping: 10,
        stiffness: 250,
        mass: 1,
      },
    },
  };

  // Variantes opcionales para aparición progresiva de configuraciones
  const settingsVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: "easeOut", delay: 0.1 },
    },
  };

  // Variantes simples para la transición entre vistas (sin animaciones complejas)
  const viewTransitionVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.2, ease: "easeIn" },
    },
  };

  return (
    <AnimatePresence>
      {/* Overlay semitransparente para bloquear interacción con el fondo */}
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-30 z-40 filter blur-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      />

      <motion.div
        className={`fixed ${
          isMobile
            ? "bottom-0 left-0 right-0"
            : "bottom-4 left-4 right-4 md:right-auto md:max-w-[600px]"
        } z-50`}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <motion.div
          className={`bg-white ${
            isMobile ? "rounded-none" : "rounded-xl"
          } shadow-2xl border border-gray-200 max-h-[60vh] overflow-hidden`}
          style={{
            backgroundColor: brandingConfig.colors.white,
            borderRadius: isMobile ? "0" : "0.8rem",
          }}
          variants={expansionVariants}
          animate={showSettings ? "expanded" : "compact"}
        >
          <div className="cookies-banner-container p-6 max-h-[60vh] overflow-y-auto">
            <AnimatePresence mode="wait">
              {!showSettings ? (
                // Vista inicial del banner
                <motion.div
                  key="initial-view"
                  variants={viewTransitionVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <h3
                    className="text-xl font-bold mb-4"
                    style={{ color: brandingConfig.colors.primary }}
                  >
                    POLÍTICA DE COOKIES
                  </h3>

                  <p
                    className="text-base mb-6 leading-relaxed"
                    style={{ color: brandingConfig.colors.primary }}
                  >
                    Utilizamos cookies y tecnologías similares para mejorar tu
                    experiencia en nuestro sitio web, para crear contenidos,
                    anuncios y ofertas personalizados para ti y para analizar el
                    uso del sitio web. Haznos saber tus preferencias. Si quieres
                    saber más, consulta nuestra{" "}
                    <a
                      href="https://zerolatencyvr.com/cookies"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:no-underline"
                      style={{ color: brandingConfig.colors.link }}
                    >
                      Política de cookies
                    </a>
                    .
                  </p>

                  <div
                    className={`flex ${
                      isMobile ? "flex-col" : "flex-row"
                    } gap-4 mb-4`}
                  >
                    <Button
                      onClick={handleAcceptAll}
                      variant="solid"
                      background="primary"
                      color="white"
                      size={isMobile ? "medium" : "small"}
                      className={isMobile ? "w-full" : ""}
                    >
                      ACEPTAR TODO
                    </Button>

                    <Button
                      onClick={handleRejectAll}
                      variant="outline"
                      color={brandingConfig.colors.primary}
                      size={isMobile ? "medium" : "small"}
                      className={isMobile ? "w-full" : ""}
                    >
                      RECHAZAR TODO
                    </Button>
                  </div>

                  <button
                    onClick={() => setShowSettings(true)}
                    className={`text-sm cursor-pointer font-bold hover:text-secondary ${
                      isMobile ? "w-full text-center py-2" : ""
                    }`}
                    style={{ color: brandingConfig.colors.primary }}
                  >
                    GESTIONAR PREFERENCIAS DE COOKIES
                  </button>
                </motion.div>
              ) : (
                // Vista expandida de configuración
                <motion.div
                  key="settings-view"
                  variants={viewTransitionVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3
                      className="text-xl font-bold"
                      style={{ color: brandingConfig.colors.primary }}
                    >
                      CONFIGURACIÓN DE COOKIES
                    </h3>

                    <button
                      onClick={() => setShowSettings(false)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      aria-label="Cerrar configuración"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={brandingConfig.colors.primary}
                        strokeWidth="2"
                      >
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <p
                    className="text-base mb-6 leading-relaxed italic"
                    style={{ color: brandingConfig.colors.primary }}
                  >
                    Este sitio utiliza cookies y tecnologías similares para
                    brindarle la mejor experiencia de navegación, crear
                    contenido, anuncios y ofertas personalizados para usted y
                    analizar el uso del sitio web. Obtenga más información en
                    nuestra{" "}
                    <a
                      href="https://zerolatencyvr.com/cookies"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:no-underline"
                      style={{ color: brandingConfig.colors.link }}
                    >
                      Política de cookies
                    </a>
                    .
                  </p>

                  <div
                    className={`flex ${
                      isMobile ? "flex-col" : "flex-row"
                    } gap-4 mb-6`}
                  >
                    <Button
                      onClick={handleAcceptAll}
                      variant="solid"
                      background="primary"
                      color="white"
                      size={isMobile ? "medium" : "small"}
                      className={isMobile ? "w-full" : ""}
                    >
                      ACEPTAR TODO
                    </Button>

                    <Button
                      onClick={handleRejectAll}
                      variant="outline"
                      color={brandingConfig.colors.primary}
                      size={isMobile ? "medium" : "small"}
                      className={isMobile ? "w-full" : ""}
                    >
                      RECHAZAR TODO
                    </Button>
                  </div>

                  <h6
                    className="text-lg font-semibold mb-4"
                    style={{ color: brandingConfig.colors.primary }}
                  >
                    GESTIONAR PREFERENCIAS DE COOKIES
                  </h6>

                  {/* Configuración de cookies individuales */}
                  <div className="space-y-6">
                    {/* Cookies esenciales */}
                    <motion.div variants={settingsVariants}>
                      <div className="flex justify-between items-center mb-2">
                        <p
                          className="font-medium"
                          style={{ color: brandingConfig.colors.primary }}
                        >
                          Cookies esenciales
                        </p>
                        <span
                          className="text-sm font-semibold"
                          style={{ color: brandingConfig.colors.primary }}
                        >
                          ACTIVE
                        </span>
                      </div>
                      <p
                        className="text-sm leading-relaxed"
                        style={{ color: brandingConfig.colors.primary }}
                      >
                        Nuestro sitio web no funcionará correctamente sin las
                        cookies esenciales. Te ayudan a navegar por las áreas
                        seguras del sitio web, permitiéndote hacer cosas como
                        usar tu carrito de compras. Deshabilitar estas cookies
                        puede hacer que ciertas funciones y servicios no estén
                        disponibles.
                      </p>
                    </motion.div>

                    {/* Cookies de rendimiento */}
                    <motion.div variants={settingsVariants}>
                      <div className="flex justify-between items-center mb-2">
                        <p
                          className="font-medium"
                          style={{ color: brandingConfig.colors.primary }}
                        >
                          Cookies de rendimiento
                        </p>
                        <Switch
                          id="performance-cookies"
                          checked={cookiePreferences.performance}
                          onChange={(checked) =>
                            handlePreferenceChange("performance", checked)
                          }
                        />
                      </div>
                      <p
                        className="text-sm leading-relaxed"
                        style={{ color: brandingConfig.colors.primary }}
                      >
                        Estas cookies nos dan una visión general del rendimiento
                        de nuestro sitio web y de cómo lo utilizan los
                        visitantes. Utilizamos esta información para ayudarnos a
                        mejorar nuestro sitio web.
                      </p>
                    </motion.div>

                    {/* Cookies funcionales */}
                    <motion.div variants={settingsVariants}>
                      <div className="flex justify-between items-center mb-2">
                        <p
                          className="font-medium"
                          style={{ color: brandingConfig.colors.primary }}
                        >
                          Cookies funcionales
                        </p>
                        <Switch
                          id="functional-cookies"
                          checked={cookiePreferences.functional}
                          onChange={(checked) =>
                            handlePreferenceChange("functional", checked)
                          }
                        />
                      </div>
                      <p
                        className="text-sm leading-relaxed"
                        style={{ color: brandingConfig.colors.primary }}
                      >
                        Las cookies funcionales ayudan a la hora de probar
                        nuevas versiones del sitio web. También se pueden
                        utilizar para proporcionar herramientas que ayuden a
                        mejorar su experiencia de navegación, incluida la
                        personalización de nuestro sitio web.
                      </p>
                    </motion.div>

                    {/* Cookies de marketing */}
                    <motion.div variants={settingsVariants}>
                      <div className="flex justify-between items-center mb-2">
                        <p
                          className="font-medium"
                          style={{ color: brandingConfig.colors.primary }}
                        >
                          Cookies de marketing
                        </p>
                        <Switch
                          id="marketing-cookies"
                          checked={cookiePreferences.marketing}
                          onChange={(checked) =>
                            handlePreferenceChange("marketing", checked)
                          }
                        />
                      </div>
                      <p
                        className="text-sm leading-relaxed"
                        style={{ color: brandingConfig.colors.primary }}
                      >
                        Estas cookies recopilan información sobre sus hábitos de
                        navegación para mostrarle anuncios relevantes para sus
                        intereses, en función de los productos y servicios que
                        haya visto. Esto puede incluir el intercambio de datos
                        recopilados mediante dichas cookies con terceros para
                        permitir el remarketing (retargeting) en sitios web
                        externos.
                      </p>
                    </motion.div>
                  </div>

                  <div
                    className={`flex ${
                      isMobile ? "flex-col" : "flex-row"
                    } gap-4 mt-6`}
                  >
                    <Button
                      onClick={handleSavePreferences}
                      variant="solid"
                      background="primary"
                      color="white"
                      size={isMobile ? "medium" : "small"}
                      className={isMobile ? "w-full" : ""}
                    >
                      GUARDAR PREFERENCIAS
                    </Button>

                    <Button
                      onClick={() => setShowSettings(false)}
                      variant="outline"
                      color={brandingConfig.colors.primary}
                      size={isMobile ? "medium" : "small"}
                      className={isMobile ? "w-full" : ""}
                    >
                      CANCELAR
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookiesBanner;
